import { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSketchStore, createSketchSolid } from '../../state/useSketchStore';
import {
    unionSolids, subtractSolids, intersectSolids, trimSolids, splitSolids,
} from '../../core/sketch/solidOps';
import { notify } from '../../utils/notify';
import { useSketchToolEnv } from './sketchInput';
import { registerSketchTool, type SketchHit } from './sketchToolBus';

/**
 * Solid Tools (SketchUp parity): Union, Subtract, Intersect, Trim, Split
 * and Outer Shell as click-pick tools — first click picks solid A, second
 * picks solid B, the boolean runs and the inputs are replaced by the
 * result shells. A rejected watertight guard keeps both inputs untouched.
 */

type Op = 'union' | 'subtract' | 'intersect' | 'trim' | 'split' | 'outerShell';

const OP_LABEL: Record<Op, string> = {
    union: 'Union',
    subtract: 'Subtract',
    intersect: 'Intersect',
    trim: 'Trim',
    split: 'Split',
    outerShell: 'Outer Shell',
};

function runSolidOp(op: Op, aId: string, bId: string): void {
    const store = useSketchStore.getState();
    const a = store.solids[aId];
    const b = store.solids[bId];
    if (!a || !b) return;

    const result = op === 'union' || op === 'outerShell'
        ? unionSolids([a, b])
        : op === 'subtract'
            ? subtractSolids(a, b)
            : op === 'intersect'
                ? intersectSolids(a, b)
                : op === 'trim'
                    ? trimSolids(a, b)
                    : splitSolids(a, b);

    if (!result.ok || !result.shells) {
        notify(`${OP_LABEL[op]} rejected: ${result.reason ?? 'unknown error'}`, 'warning');
        return;
    }

    const label = OP_LABEL[op];
    if (op === 'trim') {
        // Trim keeps the cutter (A); only the target (B) is replaced.
        store.removeSolids([bId], label);
        result.shells.forEach((faces) => {
            store.addSolid(createSketchSolid(faces, label), label);
        });
        return;
    }
    if (op === 'split') {
        // Both inputs die; every piece (A-B, A∩B, B-A) is created.
        store.removeSolids([aId, bId], label);
        result.shells.forEach((faces, i) => {
            store.addSolid(createSketchSolid(faces, `${label} ${i + 1}`), label);
        });
        return;
    }
    // Union / Outer Shell / Subtract / Intersect: inputs die, one shell born.
    store.removeSolids([aId, bId], label);
    store.addSolid(createSketchSolid(result.shells[0], label), label);
}

/** Shared pick-two flow for all solid operations. */
function useSolidOpTool(op: Op) {
    const env = useSketchToolEnv();
    const picksRef = useRef<string[]>([]);
    const [picks, setPicks] = useState<string[]>([]);
    const [cursor, setCursor] = useState<THREE.Vector3 | null>(null);

    useEffect(() => {
        registerSketchTool({
            onMove: (hit) => setCursor(hit.point.clone()),
            onDown: (hit: SketchHit) => {
                if (hit.button !== 0) return;
                if (!hit.solidId) {
                    if (hit.kind === 'plane') {
                        // Empty space re-arms the picker.
                        picksRef.current = [];
                        setPicks([]);
                    }
                    return;
                }
                const solid = useSketchStore.getState().solids[hit.solidId];
                if (!solid || solid.locked) return;
                if (picksRef.current.includes(hit.solidId)) return;
                const next = [...picksRef.current, hit.solidId];
                if (next.length === 2) {
                    picksRef.current = [];
                    setPicks([]);
                    runSolidOp(op, next[0], next[1]);
                    return;
                }
                picksRef.current = next;
                setPicks(next);
            },
        });
        return () => registerSketchTool(null);
    }, [env, op]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== 'Escape') return;
            picksRef.current = [];
            setPicks([]);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return { picks, cursor };
}

export function useSketchUnionTool(): { picks: string[]; cursor: THREE.Vector3 | null } {
    return useSolidOpTool('union');
}

export function useSketchSubtractTool(): { picks: string[]; cursor: THREE.Vector3 | null } {
    return useSolidOpTool('subtract');
}

export function useSketchIntersectTool(): { picks: string[]; cursor: THREE.Vector3 | null } {
    return useSolidOpTool('intersect');
}

export function useSketchTrimTool(): { picks: string[]; cursor: THREE.Vector3 | null } {
    return useSolidOpTool('trim');
}

export function useSketchSplitTool(): { picks: string[]; cursor: THREE.Vector3 | null } {
    return useSolidOpTool('split');
}

export function useSketchOuterShellTool(): { picks: string[]; cursor: THREE.Vector3 | null } {
    return useSolidOpTool('outerShell');
}
